import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Send, Users, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";

interface InviteNetworkDialogProps {
  open: boolean;
  onClose: () => void;
  sprintId: string;
  sprintObjective: string;
  userId: string;
  onInvitationsSent: (count: number) => void;
}

export const InviteNetworkDialog = ({
  open,
  onClose,
  sprintId,
  sprintObjective,
  userId,
  onInvitationsSent,
}: InviteNetworkDialogProps) => {
  const { toast } = useToast();
  
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [manualEmails, setManualEmails] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [alreadyInvited, setAlreadyInvited] = useState<string[]>([]);

  // Fetch all platform users
  const { data: allProfiles = [], isLoading: loadingNetwork } = useQuery({
    queryKey: ['all-profiles-sprint', userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name, username, avatar_url, title, organization, email')
        .order('display_name');
      
      if (error) throw error;
      return data;
    },
    enabled: !!userId && open
  });

  // Filter out current user
  const platformUsers = allProfiles.filter(p => p.id !== userId);

  // Load existing invitations when dialog opens
  useEffect(() => {
    if (open && sprintId) {
      loadExistingInvitations();
      // Reset selections
      setSelectedUserIds([]);
      setSearchTerm("");
      setManualEmails("");
      setCustomMessage("");
    }
  }, [open, sprintId]);

  const loadExistingInvitations = async () => {
    try {
      const { data: existingInvites, error } = await supabase
        .from("sprint_invitations")
        .select("invitee_email, invitee_user_id")
        .eq("sprint_id", sprintId);

      if (error) throw error;

      const invitedUserIds = (existingInvites || [])
        .map(inv => inv.invitee_user_id)
        .filter(Boolean);
      
      setAlreadyInvited(invitedUserIds);
    } catch (error) {
      console.error("Error loading existing invitations:", error);
    }
  };

  const handleSendInvitations = async () => {
    setSending(true);

    try {
      // Parse manual emails
      const manualEmailList = manualEmails
        .split(",")
        .map(e => e.trim())
        .filter(e => e.length > 0);

      if (selectedUserIds.length === 0 && manualEmailList.length === 0) {
        toast({
          title: "No recipients selected",
          description: "Please select at least one person to invite",
          variant: "destructive",
        });
        setSending(false);
        return;
      }

      let sentCount = 0;

      // Get current user's profile for inviter name
      const { data: inviterProfile } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', userId)
        .single();

      const inviterName = inviterProfile?.display_name || 'Unknown';

      // Handle selected platform users (loop through all selected)
      for (const selectedUserId of selectedUserIds) {
        if (!alreadyInvited.includes(selectedUserId)) {
          const selectedUser = platformUsers.find(u => u.id === selectedUserId);
          
          if (selectedUser) {
            // Create sprint invitation record
            const { data: invitationData, error: inviteError } = await supabase
              .from("sprint_invitations")
              .insert({
                sprint_id: sprintId,
                inviter_id: userId,
                invitee_user_id: selectedUser.id,
                invitee_email: selectedUser.email || '',
                invitee_name: selectedUser.display_name,
                status: 'sent',
                invitation_message: customMessage || null
              })
              .select()
              .single();

            if (inviteError) {
              console.error("Error creating invitation:", inviteError);
            } else {
              // Create in-platform notification with interactive metadata
              const { error: notifError } = await supabase
                .from("notifications")
                .insert({
                  user_id: selectedUser.id,
                  type: 'sprint_invitation',
                  title: 'Sprint Invitation',
                  message: `${inviterName} invited you to contribute to a sprint: ${sprintObjective}`,
                  action_url: null, // Will be handled by dialog
                  metadata: {
                    sprint_id: sprintId,
                    inviter_id: userId,
                    inviter_name: inviterName,
                    invitation_id: invitationData.id,
                    sprint_objective: sprintObjective,
                    custom_message: customMessage || null,
                    requires_response: true
                  }
                });

              if (notifError) {
                console.error("Error creating notification:", notifError);
              } else {
                // Create sent notification for the sender to track
                await supabase
                  .from("sent_notifications")
                  .insert({
                    sender_id: userId,
                    recipient_id: selectedUser.id,
                    type: 'sprint_invitation',
                    title: 'Sprint Invitation Sent',
                    message: `You invited ${selectedUser.display_name} to sprint: ${sprintObjective}`,
                    read: false,
                    metadata: {
                      sprint_id: sprintId,
                      invitee_id: selectedUser.id,
                      invitee_name: selectedUser.display_name,
                      invitation_id: invitationData.id,
                      sprint_objective: sprintObjective,
                      custom_message: customMessage || null
                    },
                    action_url: `/prompt-architecture-sprint?sprint=${sprintId}`
                  });
                
                sentCount++;
              }
            }
          }
        }
      }

      // Handle manual email invitations (fallback to email)
      if (manualEmailList.length > 0) {
        const { data: emailData, error: emailError } = await supabase.functions.invoke("send-sprint-invitation", {
          body: {
            sprint_id: sprintId,
            invitee_emails: manualEmailList,
            custom_message: customMessage || null,
          },
        });

        if (!emailError && emailData) {
          sentCount += emailData.sent;
        }
      }

      toast({
        title: "Invitations sent!",
        description: `${sentCount} invitation${sentCount !== 1 ? "s" : ""} sent successfully`,
      });

      onInvitationsSent(sentCount);
      
      // Reset form
      setSelectedUserIds([]);
      setSearchTerm("");
      setManualEmails("");
      setCustomMessage("");
      onClose();
    } catch (error: any) {
      console.error("Error sending invitations:", error);
      toast({
        title: "Failed to send invitations",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  // Filter users based on search
  const filteredUsers = platformUsers.filter(user =>
    user.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.organization?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedCount = selectedUserIds.length + 
    manualEmails.split(",").filter(e => e.trim().length > 0).length;

  // Handlers for multi-select
  const handleToggleUser = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    const selectableUsers = filteredUsers
      .filter(u => !alreadyInvited.includes(u.id))
      .map(u => u.id);
    setSelectedUserIds(selectableUsers);
  };

  const handleDeselectAll = () => {
    setSelectedUserIds([]);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Invite Your Network to Sprint
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-2">
            <strong>Objective:</strong> {sprintObjective}
          </p>
        </DialogHeader>

        <Tabs defaultValue="network" className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="network">
              Platform Members
            </TabsTrigger>
            <TabsTrigger value="manual">Add by Email</TabsTrigger>
          </TabsList>

          <TabsContent value="network" className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Select members to invite:</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleSelectAll}
                    disabled={loadingNetwork || sending || filteredUsers.filter(u => !alreadyInvited.includes(u.id)).length === 0}
                  >
                    Select All
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleDeselectAll}
                    disabled={selectedUserIds.length === 0}
                  >
                    Clear
                  </Button>
                </div>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  disabled={loadingNetwork || sending}
                />
              </div>

              <ScrollArea className="h-[300px] border rounded-md p-4">
                {loadingNetwork ? (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Loading members...
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {searchTerm ? "No members found matching your search" : "No members available"}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredUsers.map((member) => {
                      const isInvited = alreadyInvited.includes(member.id);
                      const isSelected = selectedUserIds.includes(member.id);
                      
                      return (
                        <div
                          key={member.id}
                          className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                            isSelected ? "bg-accent border-primary" : "hover:bg-accent/50"
                          } ${isInvited ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                          onClick={() => !isInvited && !sending && handleToggleUser(member.id)}
                        >
                          <Checkbox
                            checked={isSelected}
                            disabled={isInvited || sending}
                            onCheckedChange={() => handleToggleUser(member.id)}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={member.avatar_url || undefined} />
                                <AvatarFallback>
                                  {member.display_name?.substring(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">
                                  {member.display_name}
                                </p>
                                {(member.title || member.organization) && (
                                  <p className="text-xs text-muted-foreground truncate">
                                    {[member.title, member.organization].filter(Boolean).join(" • ")}
                                  </p>
                                )}
                              </div>
                            </div>
                            {isInvited && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Already invited
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>

              <p className="text-xs text-muted-foreground">
                {selectedUserIds.length} of {filteredUsers.filter(u => !alreadyInvited.includes(u.id)).length} members selected
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="custom-message-network">
                Add a personal message (optional)
              </Label>
              <Textarea
                id="custom-message-network"
                placeholder="I'd love your expertise on this sprint..."
                value={customMessage}
                onChange={e => setCustomMessage(e.target.value)}
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground text-right">
                {customMessage.length}/500 characters
              </p>
            </div>
          </TabsContent>

          <TabsContent value="manual" className="space-y-4">
            <div>
              <Label htmlFor="manual-emails">
                Email Addresses (comma-separated)
              </Label>
              <Textarea
                id="manual-emails"
                placeholder="colleague1@example.com, colleague2@example.com"
                value={manualEmails}
                onChange={e => setManualEmails(e.target.value)}
                rows={6}
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Separate multiple email addresses with commas
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="custom-message-email">
                Add a personal message (optional)
              </Label>
              <Textarea
                id="custom-message-email"
                placeholder="I'd love your expertise on this sprint..."
                value={customMessage}
                onChange={e => setCustomMessage(e.target.value)}
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground text-right">
                {customMessage.length}/500 characters
              </p>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex items-center justify-between text-sm">
          <p className="text-muted-foreground">
            {selectedCount} recipient{selectedCount !== 1 ? "s" : ""} selected
          </p>
          {selectedCount > 10 && (
            <p className="text-destructive text-xs">
              Maximum 10 invitations per sprint
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSendInvitations}
            disabled={selectedCount === 0 || selectedCount > 10 || sending}
          >
            {sending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Send Invitations
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
